import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  PaydateInfo,
  ReportDownloadArgs,
  ReportGeneratedArgs,
  ReportPaydate,
  TaxActivityByAgencyReportArgs
} from '../../../../models/reports';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CompaniesService } from '../../../../services/companies.service';
import moment from 'moment';
import {
  formatDatePickerValue,
  parseDatePickerValue
} from '../../../../utils/datepicker-value-parser';
import { dateRangeValidator } from '../../../../utils/dateRangeValidator';
import { forkJoin, Observable } from 'rxjs';
import { PayrollTotals } from '../../../../models/client';
import { GetQuarter, GetQuarterEnd, GetQuarterStart } from '../../../../utils/quarter-utils';
import { ReportsService } from '../../../../services/reports.service';
import { QuarterInfo } from '../../../../components/quarter-picker/quarter-picker.component';

@Component({
  selector: 'lib-tax-liability-by-agency',
  templateUrl: './tax-liability-by-agency.component.html',
  styleUrls: ['./tax-liability-by-agency.component.css'],
  standalone: false
})
export class TaxLiabilityByAgencyComponent implements OnInit {
  @Input()
  public clientCode: string;
  @Output()
  public reportGenerated = new EventEmitter<ReportGeneratedArgs>();
  @Output()
  public reportDownloadClicked = new EventEmitter<ReportDownloadArgs>();
  public taxAgencies: string[];
  public lastPayrollTotals: any[];
  public minimumValue: any;
  public maximumValue: any;
  private readonly ENDPOINT = 'TaxLiabilityByAgency';
  public reportGenerationForm: UntypedFormGroup;
  public validQuarters: Map<number, QuarterInfo>;
  constructor(
    private fb: UntypedFormBuilder,
    private companiesService: CompaniesService,
    private reportService: ReportsService
  ) {}

  ngOnInit(): void {
    const obs: Observable<any>[] = [
      this.companiesService.getTaxAgencies(this.clientCode),
      this.companiesService.getLastPayrollTotals(this.clientCode, 0),
      this.reportService.getPayrollHistory(this.clientCode)
    ];

    forkJoin(obs).subscribe(result => {
      this.taxAgencies = result[0];
      this.lastPayrollTotals = result[1];
      const x: ReportPaydate[] = result[2];
      const maxDate: Date = new Date();
      const sortedYears = x.sort((a, b) => {
        if (a.Year < b.Year) return -1;
        if (a.Year > b.Year) return 1;
        return 0;
      });
      let minYear = maxDate.getFullYear();
      if (sortedYears.length > 0) {
        minYear = sortedYears[0].Year;
      }
      const minDate: Date = new Date(minYear, 0, 1);
      this.minimumValue = {
        year: minDate.getFullYear(),
        value: GetQuarter(minDate)
      };
      this.maximumValue = {
        year: maxDate.getFullYear(),
        value: GetQuarter(maxDate)
      };
      this._groupByQuarters(x);
      this._initForm();
    });
  }
  public generateReport() {
    const args = this._getModel();
    this.reportGenerated.emit({ args, endpoint: this.ENDPOINT });
  }

  public downloadReport(format: string) {
    const args = this._getModel();
    const quarter = this.reportGenerationForm.get('quarter').value;
    const now = moment().format('MMDDYYYY');
    this.reportDownloadClicked.emit({
      args,
      endpoint: this.ENDPOINT,
      filename: `Tax Liability By Agency_${this.clientCode}_${quarter.year} Q${quarter.quarter}_${now}.${format}`,
      format
    });
  }
  private _getModel() {
    const quarter = this.reportGenerationForm.get('quarter').value;
    const start = GetQuarterStart(quarter.quarter, quarter.year);
    const end = GetQuarterEnd(quarter.quarter, quarter.year);

    const args: TaxActivityByAgencyReportArgs = {
      StartDate: start,
      EndDate: end,
      TaxAgency: this.reportGenerationForm.get('taxAgency').value
    };
    return args;
  }
  private _initForm() {
    let payDate: Date;
    if (this.lastPayrollTotals.length > 0) {
      payDate = new Date(Date.parse(this.lastPayrollTotals[0].PayDate));
    } else {
      payDate = new Date();
    }
    const start = GetQuarter(payDate);
    let defaultAgencies = null;
    if (this.taxAgencies.length === 1) {
      defaultAgencies = this.taxAgencies[0];
    }
    this.reportGenerationForm = this.fb.group({
      taxAgency: this.fb.control(defaultAgencies, Validators.required),
      quarter: this.fb.control({ quarter: start, year: payDate.getFullYear() }, Validators.required)
    });
  }

  private _groupByQuarters(reports: ReportPaydate[]) {
    const reportMap = new Map<number, QuarterInfo>();
    reports.forEach(year => {
      const q1Start = GetQuarterStart(1, year.Year);
      const q2Start = GetQuarterStart(2, year.Year);
      const q3Start = GetQuarterStart(3, year.Year);
      const q4Start = GetQuarterStart(4, year.Year);
      const q4End = GetQuarterEnd(4, year.Year);
      reportMap.set(year.Year, {
        q1Count: year.Paydates.filter(x => {
          const paydate = new Date(Date.parse(x.Paydate));
          return paydate >= q1Start && paydate < q2Start;
        }).length,
        q2Count: year.Paydates.filter(x => {
          const paydate = new Date(Date.parse(x.Paydate));
          return paydate >= q2Start && paydate < q3Start;
        }).length,
        q3Count: year.Paydates.filter(x => {
          const paydate = new Date(Date.parse(x.Paydate));
          return paydate >= q3Start && paydate < q4Start;
        }).length,
        q4Count: year.Paydates.filter(x => {
          const paydate = new Date(Date.parse(x.Paydate));
          return paydate >= q4Start && paydate <= q4End;
        }).length
      });
    });

    console.log(reportMap);
    this.validQuarters = reportMap;
  }
}
